import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './useAuth';
import { trackButtonClick, BUTTON_ENUMS } from './userJourneyTracker';
import './WhatsAppSupportFab.css';

const DEFAULT_SUPPORT_NUMBER = '2104196594';

const FAVICON_SRC = `${process.env.PUBLIC_URL || ''}/favicon.ico`;

const CHAT_AVATAR_SRC = `${process.env.PUBLIC_URL || ''}/chat_avatar.jpeg`;

const THREAD_BG_SRC = `${process.env.PUBLIC_URL || ''}/whatsapp_background.PNG`;

function normalizeWhatsAppDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function buildWhatsAppHref(digits) {
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

function buildUserName(user) {
  if (!user) return 'there';
  const displayName = String(user.displayName || user.name || '').trim();
  if (displayName) return displayName;
  const email = String(user.email || '').trim();
  if (!email) return 'there';
  return email.split('@')[0] || 'there';
}

export default function WhatsAppSupportFab({ supportNumber, isPremiumUser = false }) {
  const { isAuthenticated, loading, user } = useAuth();
  const raw = supportNumber || DEFAULT_SUPPORT_NUMBER;
  const digits = normalizeWhatsAppDigits(raw);
  const baseHref = buildWhatsAppHref(digits);
  const userName = useMemo(() => buildUserName(user), [user]);
  const iconGradientId = `wafab-grad-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const causticDistortId = `wafab-noise-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [isOpen, setIsOpen] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const textareaRef = useRef(null);
  const [botGreeting, setBotGreeting] = useState('');
  const [greetingTypingDone, setGreetingTypingDone] = useState(false);
  const [greetingPhase, setGreetingPhase] = useState('dots');

  const messageFull = useMemo(
    () =>
      isPremiumUser
        ? `Hi ${userName}, how may I help you?`
        : '24/7 on-demand support is available for Premium members only.\n\nUpgrade to get priority help anytime.',
    [isPremiumUser, userName]
  );

  const TYPING_DOTS_MS = 1500;

  useEffect(() => {
    if (!isOpen) {
      setBotGreeting('');
      setGreetingTypingDone(false);
      setGreetingPhase('dots');
      return undefined;
    }

    setBotGreeting('');
    setGreetingTypingDone(false);
    setGreetingPhase('dots');

    const dotsTimer = window.setTimeout(() => {
      setGreetingPhase('typing');
    }, TYPING_DOTS_MS);

    return () => window.clearTimeout(dotsTimer);
  }, [isOpen, messageFull]);

  useEffect(() => {
    if (!isOpen || greetingPhase !== 'typing') {
      return undefined;
    }

    let i = 0;
    const stepMs = 38;

    const step = () => {
      i += 1;
      setBotGreeting(messageFull.slice(0, i));
      if (i >= messageFull.length) {
        setGreetingTypingDone(true);
        return false;
      }
      return true;
    };

    step();

    const id = window.setInterval(() => {
      if (!step()) {
        window.clearInterval(id);
      }
    }, stepMs);

    return () => window.clearInterval(id);
  }, [isOpen, greetingPhase, messageFull]);

  useLayoutEffect(() => {
    if (!isOpen || !isPremiumUser) {
      return;
    }
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight);
    const paddingTop = parseFloat(style.paddingTop);
    const paddingBottom = parseFloat(style.paddingBottom);
    const lh = Number.isFinite(lineHeight) && lineHeight > 0 ? lineHeight : 22;
    const pt = Number.isFinite(paddingTop) ? paddingTop : 0;
    const pb = Number.isFinite(paddingBottom) ? paddingBottom : 0;
    const minH = Math.ceil(lh + pt + pb);
    const maxH = Math.ceil(lh * 4 + pt + pb);
    el.style.height = 'auto';
    const contentH = el.scrollHeight;
    const nextH = Math.min(Math.max(contentH, minH), maxH);
    el.style.height = `${nextH}px`;
    el.style.overflowY = contentH > maxH ? 'auto' : 'hidden';
  }, [isOpen, userMessage, isPremiumUser]);

  if (loading || !isAuthenticated || !baseHref) {
    return null;
  }

  const handleOpen = () => {
    trackButtonClick(BUTTON_ENUMS.WHATSAPP_SUPPORT_FAB);
    setUserMessage('');
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setUserMessage('');
  };

  const handleSupportUpgradeClick = () => {
    trackButtonClick(BUTTON_ENUMS.WHATSAPP_SUPPORT_UPGRADE);
    handleClose();
  };

  const handleSend = () => {
    const trimmed = userMessage.trim();
    if (!trimmed) {
      return;
    }

    trackButtonClick(BUTTON_ENUMS.WHATSAPP_SUPPORT_SEND);
    const href = `${baseHref}?text=${encodeURIComponent(trimmed)}`;
    window.open(href, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
    setUserMessage('');
  };

  return (
    <>
      <button
        type="button"
        className="whatsapp-support-fab"
        style={{ ['--fab-distort-filter']: `url(#${causticDistortId})` }}
        onClick={handleOpen}
        aria-label="Chat with us on WhatsApp"
        title="Chat on WhatsApp"
      >
        <svg
          className="whatsapp-support-fab__filter-defs"
          aria-hidden="true"
          width="0"
          height="0"
        >
          <defs>
            <filter
              id={causticDistortId}
              x="-45%"
              y="-45%"
              width="190%"
              height="190%"
              colorInterpolationFilters="sRGB"
            >
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.11"
                numOctaves="2"
                seed="19"
                result="noise"
              />
              <feDisplacementMap
                in="SourceGraphic"
                in2="noise"
                scale="5"
                xChannelSelector="R"
                yChannelSelector="G"
              />
            </filter>
          </defs>
        </svg>
        <span className="whatsapp-support-fab__glass" aria-hidden="true" />
        <span className="whatsapp-support-fab__icon" aria-hidden="true">
          <svg
            width="34"
            height="34"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id={iconGradientId}
                x1="3"
                y1="2.5"
                x2="21"
                y2="21"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor="#5170ff" />
                <stop offset="48%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            {/* Shift so visual mass + main bubble sit centered in 24×24 (tail is asymmetric) */}
            <g transform="translate(0.35 1.05)">
              <g fill={`url(#${iconGradientId})`}>
                <ellipse cx="12" cy="10.35" rx="9.45" ry="8.05" />
                <path d="M 11.85 18.42 L 7.35 21.68 L 7.95 17.45 Z" />
              </g>
              <circle cx="7.85" cy="10.35" r="1.2" fill="rgba(255, 255, 255, 0.92)" />
              <circle cx="12" cy="10.35" r="1.2" fill="rgba(255, 255, 255, 0.92)" />
              <circle cx="16.15" cy="10.35" r="1.2" fill="rgba(255, 255, 255, 0.92)" />
            </g>
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="whatsapp-support-modal-overlay" onClick={handleClose} role="presentation">
          <div
            className="whatsapp-support-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="whatsapp-support-modal-heading"
            aria-describedby={
              isPremiumUser ? 'whatsapp-support-bot-msg' : 'whatsapp-support-premium-upsell'
            }
          >
            <header className="whatsapp-support-modal__header">
              <div className="whatsapp-support-modal__avatar-wrap">
                <img className="whatsapp-support-modal__avatar-img" src={FAVICON_SRC} alt="" />
                <span className="whatsapp-support-modal__online-dot" title="Online" />
              </div>
              <div className="whatsapp-support-modal__header-text">
                <h3 className="whatsapp-support-modal__title" id="whatsapp-support-modal-heading">
                  Chat support
                </h3>
              </div>
              <button
                type="button"
                className="whatsapp-support-modal__close"
                onClick={handleClose}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" />
                </svg>
              </button>
            </header>

            <div
              className={
                isPremiumUser
                  ? 'whatsapp-support-modal__thread'
                  : 'whatsapp-support-modal__thread whatsapp-support-modal__thread--bottom-chat'
              }
              style={{ ['--wa-thread-bg-image']: `url("${THREAD_BG_SRC}")` }}
            >
              <div className="whatsapp-support-modal__bot-row">
                <div className="whatsapp-support-modal__msg-avatar-wrap">
                  <img className="whatsapp-support-modal__msg-avatar" src={CHAT_AVATAR_SRC} alt="" decoding="async" />
                </div>
                {isPremiumUser ? (
                  <div
                    className="whatsapp-support-modal__bubble whatsapp-support-modal__bubble--bot"
                    id="whatsapp-support-bot-msg"
                  >
                    <span className="whatsapp-support-modal__bubble-label">Rachel</span>
                    <p className="whatsapp-support-modal__bubble-text">
                      {greetingPhase === 'dots' ? (
                        <span className="whatsapp-support-modal__typing-dots" aria-hidden="true">
                          <span className="whatsapp-support-modal__typing-dot" />
                          <span className="whatsapp-support-modal__typing-dot" />
                          <span className="whatsapp-support-modal__typing-dot" />
                        </span>
                      ) : (
                        <>
                          {botGreeting}
                          {!greetingTypingDone ? (
                            <span className="whatsapp-support-modal__type-cursor" aria-hidden="true" />
                          ) : null}
                        </>
                      )}
                    </p>
                  </div>
                ) : (
                  <div
                    className="whatsapp-support-modal__bubble whatsapp-support-modal__bubble--bot whatsapp-support-modal__bubble--upsell"
                    id="whatsapp-support-premium-upsell"
                  >
                    <span className="whatsapp-support-modal__bubble-label">Rachel</span>
                    <p className="whatsapp-support-modal__bubble-text">
                      {greetingPhase === 'dots' ? (
                        <span className="whatsapp-support-modal__typing-dots" aria-hidden="true">
                          <span className="whatsapp-support-modal__typing-dot" />
                          <span className="whatsapp-support-modal__typing-dot" />
                          <span className="whatsapp-support-modal__typing-dot" />
                        </span>
                      ) : (
                        <>
                          {botGreeting}
                          {!greetingTypingDone ? (
                            <span className="whatsapp-support-modal__type-cursor" aria-hidden="true" />
                          ) : null}
                        </>
                      )}
                    </p>
                    {greetingTypingDone ? (
                      <Link
                        to="/pricing"
                        className="whatsapp-support-modal__upgrade"
                        onClick={handleSupportUpgradeClick}
                      >
                        Upgrade to Premium
                      </Link>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {isPremiumUser ? (
              <>
                <label className="whatsapp-support-modal__composer-label" htmlFor="whatsapp-support-user-input">
                  Your message
                </label>
                <div className="whatsapp-support-modal__composer-row">
                  <textarea
                    id="whatsapp-support-user-input"
                    ref={textareaRef}
                    className="whatsapp-support-modal__textarea"
                    value={userMessage}
                    onChange={(e) => setUserMessage(e.target.value)}
                    rows={1}
                    placeholder="Type your question here"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="whatsapp-support-modal__send"
                    onClick={handleSend}
                    aria-label="Send on WhatsApp"
                  >
                    <svg
                      className="whatsapp-support-modal__send-icon"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path fill="currentColor" d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
